import { useLanguage } from '../../../features/language/useLanguage';
import { MultiSelectListbox } from './MultiSelectListbox';
import type { ItemCodeOption } from './types';

interface RecipeFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  itemCodeFilter: string[];
  onItemCodeChange: (value: string[]) => void;
  itemCodeOptions: ItemCodeOption[];
  roastTypeFilter: string[];
  onRoastTypeChange: (value: string[]) => void;
  roastTypeOptions: string[];
  machineFilter: string[];
  onMachineChange: (value: string[]) => void;
  machineOptions: string[];
  getRoastTypeLabel: (roastType?: string) => string;
}

/** Search box + item-code / roast-type / machine multi-select filters for the recipes list. */
export function RecipeFilters({
  search,
  onSearchChange,
  itemCodeFilter,
  onItemCodeChange,
  itemCodeOptions,
  roastTypeFilter,
  onRoastTypeChange,
  roastTypeOptions,
  machineFilter,
  onMachineChange,
  machineOptions,
  getRoastTypeLabel,
}: RecipeFiltersProps) {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-4 mb-4">
      <div className="flex-1">
        <label htmlFor="searchFilter" className="block text-sm font-medium text-gray-700">
          {t('purchasing.sampleList.filters.search')}
        </label>
        <input
          data-test-id="recipe-search-input"
          type="text"
          id="searchFilter"
          placeholder={t('recipes.page.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
        />
      </div>

      <MultiSelectListbox<ItemCodeOption>
        label={t('recipes.page.columns.itemCode')}
        value={itemCodeFilter}
        onChange={onItemCodeChange}
        options={itemCodeOptions}
        getOptionValue={(option) => option.code}
        renderSummary={(selected) => {
          if (selected.length === 0) return t('greenBeanProfile.allItems');
          if (selected.length === 1) {
            const found = itemCodeOptions.find((option) => option.code === selected[0]);
            return found ? `${found.name} (${found.code})` : selected[0];
          }
          return t('workOrders.filters.selectedCount', { count: selected.length });
        }}
        renderOption={(option, selected) => (
          <>
            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{option.name}</span>
            <span className="block truncate text-xs text-gray-500">{option.code}</span>
          </>
        )}
      />

      <MultiSelectListbox<string>
        label={t('workOrders.table.roastType')}
        value={roastTypeFilter}
        onChange={onRoastTypeChange}
        options={roastTypeOptions}
        getOptionValue={(option) => option}
        renderSummary={(selected) => {
          if (selected.length === 0) return t('workOrders.filters.allRoastTypes');
          if (selected.length === 1) return getRoastTypeLabel(selected[0]);
          return t('workOrders.filters.selectedCount', { count: selected.length });
        }}
        renderOption={(option, selected) => (
          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{getRoastTypeLabel(option)}</span>
        )}
      />

      <MultiSelectListbox<string>
        label={t('roast.formHeader.machine')}
        value={machineFilter}
        onChange={onMachineChange}
        options={machineOptions}
        getOptionValue={(option) => option}
        renderSummary={(selected) => {
          if (selected.length === 0) return t('workOrders.filters.allMachines');
          if (selected.length === 1) return selected[0];
          return t('workOrders.filters.selectedCount', { count: selected.length });
        }}
        renderOption={(option, selected) => (
          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{option}</span>
        )}
      />
    </div>
  );
}
